<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import DriveAPI from 'dashboard/api/drive';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const { t } = useI18n();

const files = ref([]);
const allFiles = ref([]);
const loading = ref(false);

// Statistiques calculées
const totalFiles = computed(() => files.value.length);

const totalSize = computed(() => {
  return files.value.reduce((sum, file) => sum + (file.file_size || 0), 0);
});

const humanTotalSize = computed(() => {
  const bytes = totalSize.value;
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`;
});

const recentFiles = computed(() => {
  return [...files.value]
    .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
    .slice(0, 10);
});

const largestFiles = computed(() => {
  return [...files.value]
    .sort((a, b) => (b.file_size || 0) - (a.file_size || 0))
    .slice(0, 10);
});

const maxFileSize = computed(() => {
  return largestFiles.value.length > 0 ? largestFiles.value[0].file_size : 0;
});

const getProgressWidth = fileSize => {
  if (!fileSize || !maxFileSize.value) return 2;
  const percentage = (fileSize / maxFileSize.value) * 100;
  return Math.max(percentage, 2);
};

const uniqueFolders = computed(() => {
  const folderSet = new Set();

  allFiles.value.forEach(file => {
    if (!file.folder_path || file.folder_path === '/') return;

    // Extraire tous les segments de dossiers
    const parts = file.folder_path.split('/').filter(Boolean);
    let currentPath = '';
    parts.forEach(part => {
      currentPath += `/${part}`;
      folderSet.add(currentPath);
    });
  });

  return folderSet.size;
});

const storageUsagePercent = computed(() => {
  const limit = 1024 * 1024 * 1024; // 1GB
  return Math.min((totalSize.value / limit) * 100, 100);
});

const averageFileSize = computed(() => {
  return totalFiles.value > 0 ? totalSize.value / totalFiles.value : 0;
});

// Statistiques avec tendances
const statsCards = computed(() => [
  {
    metric: t('DRIVE.REPORTS.TOTAL_FILES'),
    value: totalFiles.value.toString(),
    subtext: t('DRIVE.REPORTS.IN_DRIVE'),
    icon: 'i-lucide-files',
    iconColor: 'text-n-slate-11',
    iconBg: '',
  },
  {
    metric: t('DRIVE.REPORTS.TOTAL_SIZE'),
    value: humanTotalSize.value,
    subtext: `${storageUsagePercent.value.toFixed(1)}% ${t('DRIVE.REPORTS.USED')}`,
    icon: 'i-lucide-hard-drive',
    iconColor: 'text-n-purple-11',
    iconBg: 'bg-n-purple-3',
  },
  {
    metric: t('DRIVE.REPORTS.TOTAL_FOLDERS'),
    value: uniqueFolders.value.toString(),
    subtext: t('DRIVE.REPORTS.ORGANIZED'),
    icon: 'i-lucide-folder',
    iconColor: 'text-n-green-11',
    iconBg: 'bg-n-green-3',
  },
]);

const formatBytes = bytes => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`;
};

const formatDate = dateString => {
  const date = new Date(dateString);
  const now = new Date();
  const diff = now - date;
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString();
};

const fetchFiles = async () => {
  loading.value = true;
  try {
    const response = await DriveAPI.getAll({});
    allFiles.value = response.data;
    files.value = response.data.filter(f => f.name !== '.folder');
  } catch (error) {
    useAlert(t('DRIVE.API.ERROR.FETCH_FAILED'));
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  fetchFiles();
});
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-hidden bg-n-background">
    <!-- Header -->
    <header class="sticky top-0 z-10">
      <div
        class="flex items-center w-full py-6 px-6 gap-2 mx-auto max-w-[60rem]"
      >
        <span class="text-xl font-medium text-n-slate-12">
          {{ t('DRIVE.REPORTS.TITLE') }}
        </span>
      </div>
    </header>

    <!-- Main content -->
    <main class="flex-1 px-6 overflow-y-auto">
      <div class="w-full max-w-[60rem] mx-auto py-4 space-y-6">
        <!-- Loading -->
        <div
          v-if="loading"
          class="flex items-center justify-center py-20 text-n-slate-11"
        >
          <Spinner />
        </div>

        <!-- Overview Cards - Enhanced shadcn Stats pattern -->
        <div v-if="!loading" class="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div
            v-for="stat in statsCards"
            :key="stat.metric"
            class="group relative overflow-hidden rounded-xl border border-n-weak bg-white dark:bg-white/10 shadow-sm transition-all duration-300 hover:shadow-md hover:border-n-blue-6"
          >
            <div class="p-6">
              <div class="flex items-center justify-between">
                <div class="space-y-3 flex-1">
                  <p class="text-sm font-medium text-n-slate-11">
                    {{ stat.metric }}
                  </p>
                  <div class="space-y-1">
                    <h3
                      class="text-3xl font-bold tracking-tight text-n-slate-12 transition-colors group-hover:text-n-brand"
                    >
                      {{ stat.value }}
                    </h3>
                    <p class="text-xs text-n-slate-10">
                      {{ stat.subtext }}
                    </p>
                  </div>
                </div>
                <div
                  v-if="stat.iconBg"
                  class="flex-shrink-0 rounded-full p-3 transition-transform group-hover:scale-110"
                  :class="stat.iconBg"
                >
                  <span
                    class="size-6 transition-transform"
                    :class="[stat.icon, stat.iconColor]"
                  />
                </div>
                <span
                  v-else
                  class="size-8 transition-transform group-hover:scale-110"
                  :class="[stat.icon, stat.iconColor]"
                />
              </div>
            </div>
            <!-- Subtle gradient overlay on hover -->
            <div
              class="absolute inset-0 bg-gradient-to-br from-n-brand/0 to-n-brand/0 group-hover:from-n-brand/5 group-hover:to-transparent transition-all duration-300 pointer-events-none"
            />
          </div>
        </div>

        <!-- Storage Overview - Enhanced with Circular Progress -->
        <div
          v-if="!loading"
          class="rounded-xl border border-n-weak bg-white dark:bg-white/10 shadow-sm"
        >
          <div class="p-6 border-b border-n-weak">
            <div>
              <h3 class="text-lg font-semibold text-n-slate-12">
                {{ t('DRIVE.REPORTS.STORAGE_OVERVIEW') }}
              </h3>
              <p class="text-sm text-n-slate-10 mt-1">
                {{ t('DRIVE.REPORTS.STORAGE_OVERVIEW_DESC') }}
              </p>
            </div>
          </div>
          <div class="p-6">
            <div class="grid gap-8 md:grid-cols-[auto_1fr]">
              <!-- Circular Progress -->
              <div class="flex items-center justify-center">
                <div class="relative inline-flex items-center justify-center">
                  <svg class="size-44">
                    <circle
                      cx="88"
                      cy="88"
                      r="80"
                      stroke="currentColor"
                      stroke-width="16"
                      fill="none"
                      class="text-n-slate-3"
                    />
                    <circle
                      cx="88"
                      cy="88"
                      r="80"
                      stroke="url(#gradient)"
                      stroke-width="16"
                      fill="none"
                      stroke-dasharray="502.65"
                      :stroke-dashoffset="
                        502.65 - (502.65 * storageUsagePercent) / 100
                      "
                      stroke-linecap="round"
                      transform="rotate(-90 88 88)"
                      class="transition-all duration-1000 ease-out"
                    />
                    <defs>
                      <linearGradient
                        id="gradient"
                        x1="0%"
                        y1="0%"
                        x2="100%"
                        y2="100%"
                      >
                        <stop
                          offset="0%"
                          class="text-n-brand"
                          stop-color="currentColor"
                        />
                        <stop
                          offset="100%"
                          class="text-n-purple-11"
                          stop-color="currentColor"
                        />
                      </linearGradient>
                    </defs>
                  </svg>
                  <div class="absolute flex flex-col items-center">
                    <span class="text-3xl font-bold text-n-slate-12">
                      {{ storageUsagePercent.toFixed(1) }}%
                    </span>
                    <span class="text-xs text-n-slate-10 mt-1">
                      {{ humanTotalSize }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Storage Details -->
              <div class="space-y-4">
                <div class="space-y-2">
                  <div class="flex items-center justify-between text-sm">
                    <span class="text-n-slate-11 font-medium">
                      {{ t('DRIVE.REPORTS.USED_SPACE') }}
                    </span>
                    <span class="font-bold text-n-brand">
                      {{ humanTotalSize }}
                    </span>
                  </div>
                  <div
                    class="relative w-full bg-n-slate-3 rounded-full h-3 overflow-hidden"
                  >
                    <div
                      class="h-full rounded-full bg-gradient-to-r from-n-brand to-n-purple-11 transition-all duration-1000"
                      :style="{ width: `${storageUsagePercent}%` }"
                    />
                  </div>
                </div>

                <div class="grid grid-cols-2 gap-3 pt-4 border-t border-n-weak">
                  <div class="space-y-1">
                    <p class="text-xs text-n-slate-10">
                      {{ t('DRIVE.REPORTS.TOTAL_FILES') }}
                    </p>
                    <p class="text-lg font-bold text-n-slate-12">
                      {{ totalFiles }}
                    </p>
                  </div>
                  <div class="space-y-1">
                    <p class="text-xs text-n-slate-10">
                      {{ t('DRIVE.REPORTS.TOTAL_FOLDERS') }}
                    </p>
                    <p class="text-lg font-bold text-n-slate-12">
                      {{ uniqueFolders }}
                    </p>
                  </div>
                  <div class="space-y-1">
                    <p class="text-xs text-n-slate-10">
                      {{ t('DRIVE.REPORTS.AVERAGE_FILE_SIZE') }}
                    </p>
                    <p class="text-lg font-bold text-n-slate-12">
                      {{ formatBytes(averageFileSize) }}
                    </p>
                  </div>
                  <div class="space-y-1">
                    <p class="text-xs text-n-slate-10">
                      {{ t('DRIVE.REPORTS.AVAILABLE_SPACE') }}
                    </p>
                    <p class="text-lg font-bold text-n-green-11">
                      {{ formatBytes(1024 * 1024 * 1024 - totalSize) }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Recent and Largest Files - Enhanced -->
        <div v-if="!loading" class="grid gap-4 md:grid-cols-2">
          <!-- Recent Files -->
          <div
            class="rounded-xl border border-n-weak bg-white dark:bg-white/10 shadow-sm"
          >
            <div class="p-6 border-b border-n-weak">
              <div class="flex items-center justify-between">
                <div>
                  <h3 class="text-lg font-semibold text-n-slate-12">
                    {{ t('DRIVE.REPORTS.RECENT_FILES') }}
                  </h3>
                  <p class="text-sm text-n-slate-10 mt-1">
                    {{ t('DRIVE.REPORTS.RECENT_FILES_DESC') }}
                  </p>
                </div>
                <div class="rounded-md bg-n-blue-3 p-2">
                  <span class="i-lucide-clock size-4 text-n-blue-11" />
                </div>
              </div>
            </div>
            <div class="divide-y divide-n-weak max-h-[400px] overflow-y-auto">
              <div
                v-for="(file, index) in recentFiles"
                :key="file.id"
                class="flex items-center gap-3 p-4 hover:bg-n-blue-2 dark:hover:bg-n-blue-3 transition-all cursor-pointer group"
              >
                <div
                  class="flex items-center justify-center size-8 rounded-md bg-n-alpha-2 group-hover:bg-n-blue-3 transition-colors"
                >
                  <span
                    class="size-4 text-n-slate-10 group-hover:text-n-blue-11 transition-colors"
                    :class="[file.icon_class]"
                  />
                </div>
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-medium text-n-slate-12 truncate">
                    {{ file.name }}
                  </p>
                  <p class="text-xs text-n-slate-10">
                    {{ formatDate(file.updated_at) }}
                  </p>
                </div>
                <div class="flex items-center gap-2 flex-shrink-0">
                  <span class="text-xs font-medium text-n-slate-10">
                    {{ file.human_file_size }}
                  </span>
                  <div
                    class="flex items-center justify-center size-5 rounded-full bg-n-alpha-2 text-n-slate-10"
                  >
                    <span class="text-[10px] font-medium">{{ index + 1 }}</span>
                  </div>
                </div>
              </div>
              <div
                v-if="recentFiles.length === 0"
                class="text-sm text-n-slate-10 text-center py-8"
              >
                {{ t('DRIVE.REPORTS.NO_FILES') }}
              </div>
            </div>
          </div>

          <!-- Largest Files -->
          <div
            class="rounded-xl border border-n-weak bg-white dark:bg-white/10 shadow-sm"
          >
            <div class="p-6 border-b border-n-weak">
              <div class="flex items-center justify-between">
                <div>
                  <h3 class="text-lg font-semibold text-n-slate-12">
                    {{ t('DRIVE.REPORTS.LARGEST_FILES') }}
                  </h3>
                  <p class="text-sm text-n-slate-10 mt-1">
                    {{ t('DRIVE.REPORTS.LARGEST_FILES_DESC') }}
                  </p>
                </div>
                <div class="rounded-md bg-n-slate-3 p-2">
                  <span class="i-lucide-bar-chart-2 size-4 text-n-slate-11" />
                </div>
              </div>
            </div>
            <div class="divide-y divide-n-weak max-h-[400px] overflow-y-auto">
              <div
                v-for="(file, index) in largestFiles"
                :key="file.id"
                class="p-4 space-y-2 hover:bg-n-alpha-1 dark:hover:bg-n-alpha-2 transition-all cursor-pointer group"
              >
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-3 flex-1 min-w-0">
                    <div
                      class="flex items-center justify-center size-8 rounded-md bg-n-alpha-2 group-hover:bg-n-slate-3 transition-colors"
                    >
                      <span
                        class="size-4 text-n-slate-10 group-hover:text-n-slate-11 transition-colors"
                        :class="[file.icon_class]"
                      />
                    </div>
                    <p class="text-sm font-medium text-n-slate-12 truncate">
                      {{ file.name }}
                    </p>
                  </div>
                  <div class="flex items-center gap-2 flex-shrink-0">
                    <span class="text-sm font-bold text-n-slate-12">
                      {{ file.human_file_size }}
                    </span>
                    <div
                      class="flex items-center justify-center size-5 rounded-full bg-n-alpha-2 text-n-slate-10"
                    >
                      <span class="text-[10px] font-medium">{{
                        index + 1
                      }}</span>
                    </div>
                  </div>
                </div>
                <div
                  class="relative w-full bg-n-slate-3 rounded-full h-2 overflow-hidden"
                >
                  <div
                    class="h-full rounded-full bg-gradient-to-r from-n-slate-9 to-n-slate-11 transition-all duration-700"
                    :style="{ width: `${getProgressWidth(file.file_size)}%` }"
                  />
                </div>
              </div>
              <div
                v-if="largestFiles.length === 0"
                class="text-sm text-n-slate-10 text-center py-8"
              >
                {{ t('DRIVE.REPORTS.NO_FILES') }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  </section>
</template>

<style scoped>
@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

.animate-shimmer {
  animation: shimmer 3s infinite;
}
</style>
